import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from './table.module.scss';
import { Pagination, Spinner } from '..';
import { DEFAULT_MAX_ROWS, DEFAULT_PAGE_SIZE } from './constants';

const Table = ({
  children,
  header,
  footer,
  filter,
  isEmpty,
  width,
  loading,
  rightAlignedFooterCells,
  headerCellClassNames,
  footerCellClassNames,
  pagination,
  pageSize,
  maxRows,
  serverPagination,
  page: controlledPage,
  total,
  onPageChange,
}) => {
  const [page, setPage] = useState(1);
  const isServerPagination =
    serverPagination && typeof onPageChange === 'function';
  const currentPage = isServerPagination ? controlledPage || 1 : page;
  const shouldShowLoadingState = loading;
  const shouldShowEmptyState = shouldShowLoadingState || isEmpty;
  const rows = React.Children.toArray(children).slice(0, maxRows);
  const pageCount = isServerPagination
    ? Math.ceil((total || 0) / pageSize) || 1
    : Math.ceil(rows.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const pagedRows =
    !isServerPagination && pagination && pageCount > 1
      ? rows.slice(startIndex, startIndex + pageSize)
      : rows;

  useEffect(() => {
    if (!isServerPagination && page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount, isServerPagination]);

  const row = Array.from({ length: header.length });

  const renderHeader = () =>
    header.map((col, i) => (
      <span
        className={cn(styles.headerCell, headerCellClassNames?.[i])}
        key={i}
        role="columnheader"
      >
        {filter ? (
          React.isValidElement(col) && typeof col.type !== 'string' ? (
            React.cloneElement(col, {
              disabled: loading || col.props.disabled,
            })
          ) : (
            col
          )
        ) : (
          <div className={styles.headerTitle}>{col}</div>
        )}
      </span>
    ));

  const renderFooter = () =>
    footer && !shouldShowLoadingState ? (
      <div className={styles.footerRow} role="row">
        {footer.map((col, i) => (
          <span
            className={cn(
              styles.footerCell,
              rightAlignedFooterCells.includes(i) && styles.rightCells,
              footerCellClassNames?.[i]
            )}
            key={i}
            role="gridcell"
          >
            {col}
          </span>
        ))}
      </div>
    ) : null;

  const renderEmptyRow = () =>
    shouldShowEmptyState && (
      <div className={styles.emptyRow}>
        {row.map((_, i) => (
          <TableCell key={i}> </TableCell>
        ))}
      </div>
    );

  return (
    <div className={styles.main}>
      <div className={styles.tableWrapper}>
        <div className={cn(styles.table, styles[width])} role="grid">
          <div className={styles.header} role="presentation">
            <div className={styles.headerRow} role="row">
              {renderHeader()}
            </div>
          </div>
          <div className={styles.body} role="presentation">
            {!shouldShowLoadingState && pagedRows}
          </div>
          {renderEmptyRow()}
          {renderFooter()}
        </div>
      </div>
      {pagination && pageCount > 1 && (
        <div className={styles.paginationRow}>
          <Pagination
            loading={loading}
            page={currentPage}
            pageCount={pageCount}
            onChange={isServerPagination ? onPageChange : setPage}
          />
        </div>
      )}
      {shouldShowEmptyState && (
        <div className={styles.infoText}>
          {loading ? <Spinner /> : 'Rezultatų nėra'}
        </div>
      )}
    </div>
  );
};

export const TableRow = ({
  children,
  url,
  onClick,
  disabled,
  largeFont,
  state,
  className,
}) =>
  url ? (
    <Link
      onClick={onClick}
      className={cn(
        styles.bodyRow,
        className,
        disabled ? styles.disabled : null,
        largeFont ? styles['font-big'] : ''
      )}
      to={state ? { pathname: url, state } : url}
      role="row"
    >
      {children}
    </Link>
  ) : (
    <div
      className={cn(
        styles.bodyRow,
        className,
        disabled ? styles.disabled : null,
        largeFont ? styles['font-big'] : ''
      )}
      onClick={onClick}
      role="row"
    >
      {children}
    </div>
  );

export const TableSeparator = ({ columns }) => (
  <div className={styles.separatorRow}>
    {columns.map((_, i) => (
      <TableCell key={i}> </TableCell>
    ))}
  </div>
);

export const TableCell = ({
  children,
  align,
  verticalCenter,
  className,
  onClick,
}) => (
  <span
    className={cn(
      styles.bodyCell,
      styles[align],
      verticalCenter ? styles.verticalCenter : null,
      className
    )}
    role="gridcell"
    onClick={onClick}
  >
    {children}
  </span>
);

Table.defaultProps = {
  isEmpty: false,
  footer: null,
  filter: false,
  loading: false,
  width: 'xs',
  rightAlignedFooterCells: [],
  headerCellClassNames: [],
  footerCellClassNames: [],
  pagination: true,
  pageSize: DEFAULT_PAGE_SIZE,
  maxRows: DEFAULT_MAX_ROWS,
  serverPagination: false,
  page: 1,
  total: 0,
};

Table.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.array.isRequired,
  footer: PropTypes.array,
  filter: PropTypes.bool,
  isEmpty: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  width: PropTypes.oneOf(['xs', 'sm', 'lg']),
  state: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  pagination: PropTypes.bool,
  pageSize: PropTypes.number,
  maxRows: PropTypes.number,
  serverPagination: PropTypes.bool,
  page: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  headerCellClassNames: PropTypes.arrayOf(PropTypes.string),
  footerCellClassNames: PropTypes.arrayOf(PropTypes.string),
};

TableRow.defaultProps = {
  url: null,
  onClick: () => {},
  disabled: false,
  largeFont: false,
  className: '',
};

TableRow.propTypes = {
  url: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  largeFont: PropTypes.bool,
  className: PropTypes.string,
};

TableCell.defaultProps = {
  children: null,
  align: 'left',
  verticalCenter: false,
  className: '',
};

TableCell.propTypes = {
  children: PropTypes.node,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default Table;
